import { getFieldValue } from 'components/FilterPlates/FilterPlate/drillDownDescriptions';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';

export const describeDateOffset = (offset: V5SeriesDateOffset): string => {
  const { type, count } = offset;
  if (type === 'lastYear') {
    return count === 1 ? 'Last year' : `${count} years ago`;
  }
  return count === 1 ? 'Previous period' : `${count} periods ago`;
};

const describePlate = ({
  plate,
  savedFilters,
}: {
  plate: FilterPlateType;
  savedFilters?: SavedFilter[];
}): string | undefined => {
  if (filterPlateTypeCheckers.isFixed(plate)) {
    return getFieldValue(plate.fixedValue) || undefined;
  }
  if (filterPlateTypeCheckers.isSavedFilter(plate)) {
    const saved = (savedFilters || []).find(
      (f) => f.id === plate.datasetFilterId,
    );
    return saved ? saved.name : 'Saved filter';
  }
  if (filterPlateTypeCheckers.isExcludeTermedDrivers(plate)) {
    return 'Excluding termed drivers';
  }
  return undefined;
};

// One thing a series asks differently from its chart: ignoring its filters,
// a filter, an earlier period or another date field. A filter that can't be
// put in words is generic ("Filter").
export interface QueryOverridePart {
  kind: 'filtersOverridden' | 'filter' | 'period' | 'dateField';
  label: string;
  isGeneric?: boolean;
}

export const describeQueryOverrideParts = ({
  overrides,
  savedFilters,
}: {
  overrides: V5SeriesQueryOverrides | undefined;
  savedFilters?: SavedFilter[];
}): QueryOverridePart[] => {
  if (!overrides) {
    return [];
  }
  const filters = (overrides.filters || []).map((plate): QueryOverridePart => {
    const label = describePlate({ plate, savedFilters });
    return label
      ? { kind: 'filter', label }
      : { kind: 'filter', label: 'Filter', isGeneric: true };
  });
  const { isFiltersOverridden, dateOffset, dateField } = overrides;
  return [
    ...(isFiltersOverridden
      ? [{ kind: 'filtersOverridden' as const, label: 'Ignores filters' }]
      : []),
    ...filters,
    ...(dateOffset && dateOffset.count > 0
      ? [{ kind: 'period' as const, label: describeDateOffset(dateOffset) }]
      : []),
    ...(dateField
      ? [{ kind: 'dateField' as const, label: `by ${dateField}` }]
      : []),
  ];
};

/**
 * A short label for what a series asks differently from its chart, e.g.
 * "Red Division · Last year". Empty when nothing is overridden.
 */
const describeQueryOverrides = ({
  overrides,
  savedFilters,
}: {
  overrides: V5SeriesQueryOverrides | undefined;
  savedFilters?: SavedFilter[];
}): string =>
  describeQueryOverrideParts({ overrides, savedFilters })
    .filter((part) => !part.isGeneric)
    .map((part) => part.label)
    .join(' · ');

export const toSeriesName = ({
  metricName,
  series,
  savedFilters,
}: {
  metricName: string;
  series: V5ChartDefinitionSeries;
  savedFilters?: SavedFilter[];
}): string => {
  if (series.displayName) {
    return series.displayName;
  }
  const summary = describeQueryOverrides({
    overrides: series.queryOverrides,
    savedFilters,
  });
  return summary ? `${metricName} · ${summary}` : metricName;
};

export default describeQueryOverrides;
